import { Box, Grid, Typography, Theme, useTheme, Divider, FormControl, InputLabel, SelectChangeEvent, Skeleton } from '@mui/material';
import { SubHeading } from '../investors/components';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Item, ItemDetails, Gridstyles } from './index';
import { Applicant, ApplicationProps, FamilyListObj, GetFamilyListResponseBody } from '../../redux-store/types/api-types';
import { formatShortDate } from '../../utils/date';
import { SxProps } from '@mui/system';
import { applicantStatusMasters, USER_ROLES } from '../../utils/constant';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { getUniqueIdentityText, isModuleAllowed, isOnboardingTypeMinor } from '../../utils/utilityFunctions';
import { showError } from '../../redux-store/actions/auth';
import { BootstrapSelect } from '../../lib/formik/SelectField';
import { renderFilteredOptions, SearchFieldWrapper } from '../../lib/formik/searchSelectField';
import { getFamilyList } from '../../redux-store/actions/onBoarding';
import { useRouteMatch } from 'react-router';

export const KeyValue = ({
  title,
  description,
  amount,
  sx = {},
  loading = false
}: {
  title: string | undefined;
  description?: string | number | boolean | null;
  amount?: number | null | undefined | string;
  sx?: SxProps<Theme>;
  loading?: boolean;
}): JSX.Element => {
  return (
    <Grid item xs={12} sm={6}>
      <Item sx={(theme: Theme) => ({
        fontSize: '16px', lineHeight: '20px', letterSpacing: '0.02px', color: theme.palette.text.secondary, pb: '7px'
      })}>{title}</Item>
      {loading && (
        <Skeleton animation="wave" width={'50%'} height={15} variant="rectangular" />
      )}
      {!loading && (
        <>
          <ItemDetails sx={(theme: Theme) => ({
            color: theme.palette.text.primary, textTransform: 'capitalize', fontWeight: '500'
          })}> {description ? description : '-'}</ItemDetails>
          {!!amount && amount !== 0 && <Item sx={{ pt: 0 }}> {amount}</Item>}
        </>
      )}
    </Grid>
  );
};

export default function PersonalDetails({
  application,
  selectedFamilyId = { key: "", value: "" },
  setSelectedFamilyId = () => { return; }
}: {
  application: ApplicationProps;
  selectedFamilyId?: { key: string, value: string };
  setSelectedFamilyId?: Dispatch<SetStateAction<{ key: string, value: string }>>;
}): JSX.Element {
  const {
    applicants = [],
    modeOfHolding,
    familyName = "",
    familyId = ""
  } = application

  const dispatch = useDispatch();

  const { path: currentPath } = useRouteMatch();
  const isComplianceApplicationDetailsScreen = currentPath.includes('compliance-application-details')

  const [familyListData, setFamilyListData] = useState<
    Array<{
      key: string;
      value: string;
    }>
  >([])
  const [query, setQuery] = useState<string>('');

  const { company = "WMS" } = useSelector((state: RootStateType) => state.navbarInfo)
  const { role = '' } = useSelector((store: RootStateType) => store.auth);

  const fetchFamilyListData = async () => {
    try {
      const response = await (dispatch(getFamilyList()) as unknown as Promise<GetFamilyListResponseBody>)

      if (!response?.isSuccess) {
        dispatch(showError(response?.message || "No Data available"))
        return
      }

      if (response?.isSuccess) {
        const modifiedFamilyListData = (response?.familyList ?? []).map((obj: FamilyListObj) => ({
          key: obj?.familyName ?? "",
          value: obj?.familyId ?? ""
        }))

        setFamilyListData(modifiedFamilyListData)
        return
      }
    } catch (error) {
      console.log("Error: ", error)
    }
  }

  useEffect(() => {
    if (
      isModuleAllowed({ value: (role ?? ""), compareArr: [USER_ROLES.AMCAPPROVER] }) &&
      !familyId &&
      !isComplianceApplicationDetailsScreen
    ) {
      fetchFamilyListData()
    }
  }, [])

  return (
    <>
      <Box
        sx={(theme: Theme) => ({
          bgcolor: theme.palette.customColors.infoCard,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '10px',
          border: `1px solid ${theme.palette.grey[100]}`,
          padding: '25px',
        })}
        mt={5}
      >
        <Box>
          <Grid container >
            <Grid item xs={12} >
              <Typography sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[500], mb: '25px' })}>
                Personal Details (Details as per KRA)
              </Typography>
            </Grid>
            <Grid item xs={12} lg={6}>
              <KeyValue title="Mode of Holding" description={modeOfHolding ?? 'Single'} />
            </Grid>
            {applicants.map((applicant, index: number) => {
              const title = [
                'First Applicant Details',
                'Second Applicant Details',
                'Third Applicant Details',
              ][index];
              return (
                <React.Fragment key={applicant.id}>
                  <SubHeading
                    sx={(theme: Theme) => ({
                      letterSpacing: '0.02px'
                    })}>
                    {isOnboardingTypeMinor(applicant?.onboardingType || '')
                      ? 'Personal Details of Guardian'
                      : title}
                  </SubHeading>
                  {/* <Gridstyles> */}
                  <Grid
                    item
                    container
                    // pb={5}
                    rowSpacing={3}
                    columnSpacing={5.5}
                    direction="row"
                  // justifyContent="space-between"
                  // alignItems="flex-start"
                  >
                    <KeyValue title={'Name'} description={`${applicant?.namePrefix ?? ""} ${applicant.name}`} />
                    <KeyValue
                      title={'Father/Spouse Name'}
                      description={applicant.fatherOrSpouseName}
                    />
                    <KeyValue title={'First Name'} description={applicant.firstName} />
                    <KeyValue title={'Last Name'} description={applicant.lastName} />
                    <KeyValue title={`${getUniqueIdentityText(company)?.pan}`} description={applicant.panNo} />
                    <KeyValue title={'Date of Birth'} description={applicant?.dateOfBirth ? formatShortDate(applicant.dateOfBirth) : "-"} />
                    <KeyValue title={'Marital Status'} description={applicant.maritalStatus} />
                    <KeyValue title={'Gender'} description={applicant.gender} />
                    <KeyValue
                      title={'Occupation Type'}
                      description={applicant.occupationDetails}
                    />
                    <KeyValue
                      title={'Status'}
                      description={
                        applicant.status
                          ? applicantStatusMasters[applicant.status]
                          : applicant.status
                      }
                    />
                    {index !== 0 && (
                      <KeyValue
                        title={'Relationship With First Applicant'}
                        description={applicant.relationShipWithFirstApplicant}
                      />
                    )}
                  </Grid>
                  {/* </Gridstyles> */}
                  {isOnboardingTypeMinor(applicant?.onboardingType || '') && (
                    <>
                      <SubHeading
                        sx={{
                          letterSpacing: 0.5 + 'px',
                        }}>
                        Personal Details of Minor Applicant
                      </SubHeading>
                      {/* <Gridstyles> */}
                      <Grid
                        item
                        container
                        rowSpacing={6}
                        columnSpacing={13}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start">
                        <KeyValue title={`${getUniqueIdentityText(company)?.pan}`} description={applicant.minorPan} />
                        <KeyValue
                          title={'Date of Birth'}
                          description={formatShortDate(applicant.minorDOB)}
                        />
                        {/* <KeyValue title={'Full Name'} description={applicant.minorName} /> */}
                        <KeyValue
                          title={'First Name'}
                          description={applicant.minorNamePrefix + ' ' + applicant.minorFirstName}
                        />
                        <KeyValue title={'Last Name'} description={applicant.minorLastName} />
                        <KeyValue title={'Father Name'} description={applicant.minorFatherName} />
                        <KeyValue title={'Gender'} description={applicant.minorGender} />
                        <KeyValue title={'Email'} description={applicant.minorEmail} />
                        <KeyValue title={'Mobile Number'} description={applicant.minorMobile} />
                        <KeyValue
                          title={'Relationship with Guardian'}
                          description={applicant.guardianRelationship}
                        />
                      </Grid>
                      {/* </Gridstyles> */}
                    </>
                  )}
                </React.Fragment>
              );
            })}
            <Grid item xs={12} my={3}>
              <Divider sx={(theme: Theme) => ({ borderColor: theme.palette.grey[100] })} />
            </Grid>
            <Grid item xs={12} sm={6} mt="-9px">
              <KeyValue title="Family Name" description={familyName} />
            </Grid>
            {(
              isModuleAllowed({
                value: (role ?? ""),
                compareArr: [USER_ROLES.AMCAPPROVER]
              }) &&
              !familyId &&
              !isComplianceApplicationDetailsScreen
            ) && (
                <Grid item xs={12} sm={6} mt={{ xs: "10px", sm: "-9px" }}>
                  <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 2 } }}>
                    <InputLabel
                      sx={(theme: Theme) => ({
                        ...theme.typography.body_500,
                      })}
                    >
                      Existing Family Name*
                    </InputLabel>
                    <BootstrapSelect
                      label="Existing Family Name*"
                      value={selectedFamilyId?.value ?? ""}
                      sx={{
                        '& .Mui-disabled': {
                          pointerEvents: 'all',
                        },
                      }}
                      MenuProps={{
                        sx: {
                          '.MuiPaper-root ': {
                            maxWidth: { xs: '100%', sm: '31%', md: '26%' },
                            maxHeight: '20%',
                            overflowX: 'auto',
                          },
                        },
                        disableAutoFocusItem: true,
                        MenuListProps: {
                          disableListWrap: true,
                        },
                      }}
                      onClose={() => {
                        setQuery('');
                      }}
                      onChange={({ target: { value } }: SelectChangeEvent<unknown>) => {
                        const selectedFamilyDetails = (familyListData ?? []).find(obj => obj?.value === value)

                        setSelectedFamilyId(selectedFamilyDetails ?? { key: "", value: "" })
                      }}
                      renderValue={(Selectedvalue) => {
                        const selectedKey = familyListData.find((_item) => _item.value == Selectedvalue);
                        return (
                          <Typography
                            variant='body_500'
                            sx={{
                              whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' }
                            }}
                            component="span">
                            {selectedKey?.key ?? ""}
                          </Typography>
                        );
                      }}
                    >
                      <SearchFieldWrapper searchFieldPlaceholder="Search Family" setQuery={setQuery} />
                      {renderFilteredOptions(familyListData, query)}
                    </BootstrapSelect>
                  </FormControl>
                </Grid>
              )}
          </Grid>
        </Box>
      </Box >
    </>
  );
}
